package com.islamicmedia.site.components

import androidx.compose.runtime.*
import com.islamicmedia.shared.domain.model.*
import com.islamicmedia.site.theme.Gray300
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Renders a markdown-formatted string with headings, bold text, numbered lists, and bullet lists
 * using Compose HTML DOM elements.
 */
@Composable
fun FormattedText(
    text: String,
    color: CSSColorValue = Color.currentColor,
    headingColor: CSSColorValue? = null,
    fontSize: CSSNumeric = 14.px,
    lineHeight: CSSNumeric = 1.7.em
) {
    var renderError by remember { mutableStateOf<String?>(null) }

    val blocks = try {
        parseMarkdownToBlocks(text)
    } catch (e: Exception) {
        // Fallback: if parsing fails (e.g. regex error), render as plain text
        println("[FormattedText] Rendering error: ${e.message}")
        renderError = e.message
        emptyList()
    }

    // Auto-report rendering errors via email (fire-and-forget)
    renderError?.let { error ->
        LaunchedEffect(error) {
            try {
                val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                val body = kotlinx.serialization.json.buildJsonObject {
                    put("userMessage", kotlinx.serialization.json.JsonPrimitive("FormattedText rendering error (auto-reported)"))
                    put("errorDetails", kotlinx.serialization.json.JsonPrimitive("Error: $error\nText preview: ${text.take(200)}"))
                    put("title", kotlinx.serialization.json.JsonPrimitive("FormattedText crash"))
                }.toString()
                kotlinx.browser.window.fetch(
                    "$serverUrl/api/error-report",
                    org.w3c.fetch.RequestInit(
                        method = "POST",
                        headers = kotlin.js.json("Content-Type" to "application/json"),
                        body = body
                    )
                )
            } catch (_: Exception) {}
        }
    }

    if (blocks.isEmpty()) {
        // Fallback: render as plain text (works even when parsing crashes)
        val fallbackColor = LocalBodyTextColor.current ?: color
        P(attrs = {
            style {
                margin(0.px)
                fontSize(fontSize)
                lineHeight(lineHeight)
                color(fallbackColor)
                property("white-space", "pre-wrap")
            }
        }) { Text(text) }
        return
    }

    MarkdownBlocks(
        blocks = blocks,
        color = color,
        headingColor = headingColor,
        fontSize = fontSize,
        lineHeight = lineHeight
    )
}

/**
 * Renders an already-parsed list of [TextBlock]s. Shared by [FormattedText] and
 * the section-decomposed concern details (so each description subsection heading
 * can be rendered — and pinned — independently). Mirrors mobile's MarkdownBlocks.
 */
@Composable
fun MarkdownBlocks(
    blocks: List<TextBlock>,
    color: CSSColorValue = Color.currentColor,
    headingColor: CSSColorValue? = null,
    fontSize: CSSNumeric = 14.px,
    lineHeight: CSSNumeric = 1.7.em
) {
    if (blocks.isEmpty()) return
    // Body-prose color honors the user's Display style ("black" darkens the readable text);
    // null keeps each caller's own default gray. Headings keep their explicit styling.
    val bodyColor = LocalBodyTextColor.current ?: color
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
        }
    }) {
        blocks.forEachIndexed { index, block ->
            when (block) {
                is TextBlock.Table -> {
                    TagElement<org.w3c.dom.HTMLElement>("table", {
                        style {
                            width(100.percent)
                            property("border-collapse", "collapse")
                            fontSize(12.px)
                            borderRadius(8.px)
                            property("overflow", "hidden")
                            property("border", "0.5px solid $Gray300")
                        }
                    }) {
                        // Header
                        TagElement<org.w3c.dom.HTMLElement>("thead", {}) {
                            TagElement<org.w3c.dom.HTMLElement>("tr", {
                                style {
                                    backgroundColor(Color("#F0F1F3"))
                                }
                            }) {
                                block.headers.forEach { header ->
                                    TagElement<org.w3c.dom.HTMLElement>("th", {
                                        style {
                                            padding(8.px, 10.px)
                                            property("text-align", "left")
                                            fontWeight(700)
                                            fontSize(12.px)
                                            color(headingColor ?: color)
                                        }
                                    }) { Text(header) }
                                }
                            }
                        }
                        // Body
                        TagElement<org.w3c.dom.HTMLElement>("tbody", {}) {
                            block.rows.forEach { row ->
                                TagElement<org.w3c.dom.HTMLElement>("tr", {
                                    style {
                                        property("border-top", "0.5px solid rgba(200,200,200,0.5)")
                                    }
                                }) {
                                    row.forEach { cell ->
                                        TagElement<org.w3c.dom.HTMLElement>("td", {
                                            style {
                                                padding(6.px, 10.px)
                                                fontSize(12.px)
                                                color(bodyColor)
                                                lineHeight(1.4.em)
                                            }
                                        }) { Text(cell) }
                                    }
                                }
                            }
                        }
                    }
                }
                is TextBlock.Heading -> {
                    if (index > 0) {
                        Hr(attrs = {
                            style {
                                property("border", "none")
                                property("border-top", "0.5px solid $Gray300")
                                margin(4.px, 0.px)
                            }
                        })
                    }
                    Div(attrs = {
                        style {
                            fontSize(13.px)
                            fontWeight(700)
                            color(headingColor ?: color)
                            property("line-height", "1.3")
                        }
                    }) { Text(block.text) }
                }
                is TextBlock.Paragraph -> {
                    P(attrs = {
                        style {
                            margin(0.px)
                            fontSize(fontSize)
                            lineHeight(lineHeight)
                            color(bodyColor)
                        }
                    }) {
                        RenderSpans(block.spans)
                    }
                }
                is TextBlock.NumberedList -> {
                    Ol(attrs = {
                        style {
                            margin(0.px)
                            paddingLeft(20.px)
                            fontSize(fontSize)
                            lineHeight(lineHeight)
                            color(bodyColor)
                        }
                    }) {
                        block.items.forEach { spans ->
                            Li(attrs = {
                                style {
                                    marginBottom(4.px)
                                }
                            }) {
                                RenderSpans(spans)
                            }
                        }
                    }
                }
                is TextBlock.BulletList -> {
                    Ul(attrs = {
                        style {
                            margin(0.px)
                            paddingLeft(20.px)
                            fontSize(fontSize)
                            lineHeight(lineHeight)
                            color(bodyColor)
                        }
                    }) {
                        block.items.forEach { spans ->
                            Li(attrs = {
                                style {
                                    marginBottom(4.px)
                                }
                            }) {
                                RenderSpans(spans)
                            }
                        }
                    }
                }
            }
        }
    }
}

/** A description section split out of a markdown string: an optional leading
 *  heading plus the body blocks that follow it (until the next heading).
 *  Mirrors mobile's MarkdownSection so each heading can be pinned independently. */
data class MarkdownSection(val heading: String?, val body: List<TextBlock>)

/** Split a markdown string into sections keyed on its headings, so each heading
 *  can be made `position: sticky` while its body scrolls. Mirrors mobile. */
fun splitMarkdownIntoSections(text: String): List<MarkdownSection> {
    val blocks = try { parseMarkdownToBlocks(text) } catch (_: Exception) { emptyList() }
    if (blocks.isEmpty()) return emptyList()
    val sections = mutableListOf<MarkdownSection>()
    var heading: String? = null
    var body = mutableListOf<TextBlock>()
    for (block in blocks) {
        if (block is TextBlock.Heading) {
            if (heading != null || body.isNotEmpty()) sections.add(MarkdownSection(heading, body.toList()))
            heading = block.text
            body = mutableListOf()
        } else {
            body.add(block)
        }
    }
    if (heading != null || body.isNotEmpty()) sections.add(MarkdownSection(heading, body.toList()))
    return sections
}

@Composable
private fun RenderSpans(spans: List<TextSpan>) {
    spans.forEach { span ->
        when (span) {
            is TextSpan.Normal -> Text(span.text)
            is TextSpan.Bold -> B { Text(span.text) }
            is TextSpan.Italic -> I { Text(span.text) }
        }
    }
}
